import React, { useState } from 'react';
import {
  Box, Button, Divider, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import HttpIcon from '@mui/icons-material/Http';
import CheckIcon from '@mui/icons-material/Check';
import {
  getApiBaseUrl,
  getApiBaseUrlOverride,
  setApiBaseUrl,
  STAFF_API_PRESETS,
} from '../../services/api';
import { store } from '../../store';
import { connectApi } from '../../services/connectApi';
import { marketplaceApi } from '../../services/marketplaceApi';

function normalizeUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

function applyApiUrl(url: string) {
  setApiBaseUrl(url);
  store.dispatch(connectApi.util.resetApiState());
  store.dispatch(marketplaceApi.util.resetApiState());
}

const ApiEndpointSelector: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [customValue, setCustomValue] = useState(() => getApiBaseUrlOverride() || getApiBaseUrl());
  const open = Boolean(anchorEl);

  const activeUrl = normalizeUrl(getApiBaseUrl());
  const hasOverride = Boolean(getApiBaseUrlOverride().trim());
  const activePreset = STAFF_API_PRESETS.find(p => normalizeUrl(p.url) === activeUrl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCustomValue(getApiBaseUrlOverride() || getApiBaseUrl());
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApplyCustom = () => {
    applyApiUrl(customValue);
    handleClose();
  };

  const handlePreset = (url: string) => {
    setCustomValue(url);
    applyApiUrl(url);
    handleClose();
  };

  const handleReset = () => {
    setCustomValue('');
    applyApiUrl('');
    handleClose();
  };

  return (
    <>
      <Tooltip title={`API: ${getApiBaseUrl()}`}>
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label="Change API endpoint"
          sx={{
            ml: 1,
            ...(hasOverride && {
              color: 'success.light',
              outline: '1px solid',
              outlineColor: 'success.light',
              outlineOffset: 2,
            }),
          }}
        >
          <HttpIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 320, maxWidth: 420, p: 1 } } }}
      >
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            API ENDPOINT
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all', mt: 0.25 }}>
            {getApiBaseUrl()}
          </Typography>
          {activePreset ? (
            <Typography variant="caption" color="success.main">
              {activePreset.label}
            </Typography>
          ) : hasOverride ? (
            <Typography variant="caption" color="warning.main">
              Custom
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Default (env)
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        {STAFF_API_PRESETS.map(preset => {
          const selected = normalizeUrl(preset.url) === activeUrl;
          return (
            <MenuItem key={preset.url} onClick={() => handlePreset(preset.url)} dense>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {selected ? <CheckIcon fontSize="small" color="success" /> : <Box sx={{ width: 20 }} />}
              </ListItemIcon>
              <ListItemText
                primary={preset.label}
                secondary={preset.url}
                secondaryTypographyProps={{ variant: 'caption', sx: { wordBreak: 'break-all' } }}
              />
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            CUSTOM URL
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="https://example.com/api/staff"
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleApplyCustom(); }}
          />
          <Box display="flex" gap={1} justifyContent="flex-end">
            {hasOverride && (
              <Button size="small" color="inherit" onClick={handleReset}>
                Reset
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleApplyCustom}
              disabled={!customValue.trim()}>
              Apply
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default ApiEndpointSelector;
